import { RowType, HeadType } from '@atlaskit/dynamic-table/dist/types/types';
import React, { useEffect, useState } from 'react';
import {
  EmptyStateProps,
  emptyStateProps as defaultEmptyStateProps,
  DEFAULT_TABLE_ROWS,
  DEFAULT_TABLE_PAGE_SELECT,
  TABLE_COLUMN_KEYS,
} from '../../constants/table';
import { CustomSearch, DateFilter, DynamicResultTable } from '@css-xp/base';
import './task-list-table.scss';
import { RequestTypeFilter, StatusFilter, UrgentFilter } from '../filters';
import Button from '@atlaskit/button';
import { isWithinInterval } from 'date-fns';

interface FormData {
  searchTerm: string;
}
interface DateRange {
  from: string | number;
  to: string | number;
}

interface FilterRows {
  searchKey: string;
  requestTypeFilter: string;
  statusFilter: string;
  dateFilter: DateRange;
  urgentFilter: boolean;
}

interface Props {
  rows?: RowType[];
  head: HeadType;
  emptyStateProps?: EmptyStateProps;
  rowsPerPage?: number;
  isLoading?: boolean;
  headingText?: string;
  searchable?: boolean;
  children?: React.ReactNode;
  requestTypeFilterOptions?: string[];
  statusFilterOptions?: string[];
  showDateFilters?: boolean;
  showUrgentFilter?: boolean;
  showResetFilter?: boolean;
  bulkActions?: React.ReactNode;
}

interface FilterRowCellType {
  key?: string | number;
  content?: React.ReactNode;
  data?: React.ReactNode;
}
export interface FilterRowType {
  cells: Array<FilterRowCellType>;
}

export const TaskListTable = ({
  rows = [],
  head,
  emptyStateProps = defaultEmptyStateProps,
  rowsPerPage: defaultRowsPerPage = DEFAULT_TABLE_ROWS,
  isLoading = false,
  headingText = '',
  searchable = true,
  children,
  requestTypeFilterOptions,
  statusFilterOptions,
  showDateFilters,
  showUrgentFilter,
  showResetFilter,
  bulkActions,
}: Props) => {
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_TABLE_PAGE_SELECT);
  const [rowsPerPage, setRowsPerPage] = useState<number>(defaultRowsPerPage);
  const [rowsToShowPerPage, setRowsToShowPerPage] = useState<RowType[]>(rows);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [requestTypeSelectedOption, setRequestTypeSelectedOption] = useState<string>('');
  const [statusSelectedOption, setStatusSelectedOption] = useState<string>('');
  const [filteredRows, setFilteredRows] = useState<RowType[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>({ from: '', to: '' });
  const [selectedDateRangeValue, setSelectedDateRangeValue] = useState<any>('');
  const [urgentChecked, setUrgentChecked] = useState<boolean>(false);

  const filterRowsObj: FilterRows = {
    searchKey: searchTerm,
    requestTypeFilter: requestTypeSelectedOption,
    statusFilter: statusSelectedOption,
    dateFilter: dateRange,
    urgentFilter: urgentChecked,
  };

  // TODO: Need to add useCallBack
  const filterRowsBySearch = (row: FilterRowType, filterBy: string): boolean => {
    for (const cell of row.cells) {
      if (
        ['number', 'string'].includes(typeof cell.data) &&
        String(cell.data).toLowerCase().includes(filterBy.toLowerCase())
      ) {
        return true;
      }
    }
    return false;
  };

  const filterDateRange = (row: FilterRowType, filterBy: DateRange, columnIndex: undefined | number): boolean => {
    if (!filterBy.from && !filterBy.to) return true;
    const cell = typeof columnIndex !== 'undefined' ? row.cells[columnIndex] : {};
    const rowDate = cell.key ? new Date(cell.key.valueOf()) : 0;
    return isWithinInterval(rowDate, {
      start: new Date(filterBy.from),
      end: new Date(filterBy.to),
    });
  };

  const filterUrgent = (row: FilterRowType, filterBy: boolean, columnIndex: undefined | number): boolean => {
    if (!filterBy) return true;
    const cellValue = typeof columnIndex !== 'undefined' ? row.cells[columnIndex].data : false;
    return !!cellValue;
  };

  const filterRows = (row: FilterRowType, filterBy: string, columnIndex: undefined | number) => {
    const cell = typeof columnIndex !== 'undefined' ? row.cells[columnIndex] : {};
    return ['string'].includes(typeof cell.data) && String(cell.data).toLowerCase().includes(filterBy.toLowerCase());
  };

  const getFilteredRows = ({ searchKey, requestTypeFilter, statusFilter, dateFilter, urgentFilter }: FilterRows) => {
    if (!searchKey && !requestTypeFilter && !statusFilter && !dateFilter.from && !dateFilter.to && !urgentFilter) {
      return rows;
    }
    let statusColumnIndex: number | undefined;
    let requestTypeColumnIndex: number | undefined;
    let dateColumnIndex: number | undefined;
    let urgentIndex: number | undefined;
    let count = 0;
    for (let index = 0; index < head.cells.length; index++) {
      const cell = head.cells[index];
      if (cell.key && String(cell.key) === TABLE_COLUMN_KEYS.REQUEST_TYPE) {
        requestTypeColumnIndex = index;
        count++;
      }
      if (cell.key && String(cell.key) === TABLE_COLUMN_KEYS.STATUS) {
        statusColumnIndex = index;
        count++;
      }
      if (cell.key && String(cell.key) === TABLE_COLUMN_KEYS.CREATED_DATE) {
        dateColumnIndex = index;
        count++;
      }
      if (cell.key && String(cell.key) === TABLE_COLUMN_KEYS.URGENT) {
        urgentIndex = index;
        count++;
      }
      if (count === 4) {
        break;
      }
    }
    return rows.filter((row) => {
      const hasSearchTermValue = filterRowsBySearch(row, searchKey);
      const hasRequestTypeValue = filterRows(row, requestTypeFilter, requestTypeColumnIndex);
      const hasStatusValue = filterRows(row, statusFilter, statusColumnIndex);
      const hasDateValue = filterDateRange(row, dateFilter, dateColumnIndex);
      const hasUrgent = filterUrgent(row, urgentFilter, urgentIndex);
      if (hasSearchTermValue && hasRequestTypeValue && hasStatusValue && hasDateValue && hasUrgent) return true;
      return false;
    });
  };

  const getSlicedRows = (_rows: RowType[], _currentPage: number, _perPageRows: number) => {
    const { startIndex, endIndex } = getIndexes(_currentPage, _perPageRows);
    return _rows.slice(startIndex, endIndex);
  };

  const getIndexes = (newPage: number, _perPageRows: number) => {
    let startIndex = 0;
    let endIndex = 0;
    if (newPage < 2) {
      startIndex = 0;
      endIndex = _perPageRows;
    } else {
      endIndex = newPage * _perPageRows;
      startIndex = endIndex - _perPageRows;
    }
    return { startIndex, endIndex };
  };

  const onPageChange = (newPage: number) => {
    setCurrentPage(newPage);
    const _rows = getSlicedRows(filteredRows, newPage, rowsPerPage);
    setRowsToShowPerPage(_rows);
  };

  const handleRowsChange = (rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(DEFAULT_TABLE_PAGE_SELECT);
    const _rows = getSlicedRows(filteredRows, DEFAULT_TABLE_PAGE_SELECT, rowsCount);
    setRowsToShowPerPage(_rows);
  };

  const onSearch: any = (formData: FormData) => {
    const searchKey = formData?.searchTerm ?? '';
    setSearchTerm(searchKey);
    setCurrentPage(DEFAULT_TABLE_PAGE_SELECT);
    filterRowsObj.searchKey = searchKey;
    filterRowsObj.requestTypeFilter = requestTypeSelectedOption;
    filterRowsObj.statusFilter = statusSelectedOption;
    filterRowsObj.dateFilter = dateRange;
    filterRowsObj.urgentFilter = urgentChecked;
    const _filteredRows = getFilteredRows(filterRowsObj as FilterRows);
    setFilteredRows(_filteredRows);
    const _rows = getSlicedRows(_filteredRows, DEFAULT_TABLE_PAGE_SELECT, rowsPerPage);
    setRowsToShowPerPage(_rows);
  };

  const handleFilters = (type: string, filterBy: any) => {
    let requestTypeFilter = requestTypeSelectedOption;
    let statusFilter = statusSelectedOption;
    let dateFilter = dateRange;
    let urgentFilter = urgentChecked;
    if (type === TABLE_COLUMN_KEYS.REQUEST_TYPE) {
      setRequestTypeSelectedOption(filterBy);
      requestTypeFilter = filterBy;
    } else if (type === TABLE_COLUMN_KEYS.CREATED_DATE) {
      setDateRange(filterBy);
      dateFilter = filterBy;
    } else if (type === TABLE_COLUMN_KEYS.URGENT) {
      setUrgentChecked(filterBy);
      urgentFilter = filterBy;
    } else {
      setStatusSelectedOption(filterBy);
      statusFilter = filterBy;
    }
    setCurrentPage(DEFAULT_TABLE_PAGE_SELECT);
    filterRowsObj.searchKey = searchTerm;
    filterRowsObj.requestTypeFilter = requestTypeFilter;
    filterRowsObj.statusFilter = statusFilter;
    filterRowsObj.dateFilter = dateFilter;
    filterRowsObj.urgentFilter = urgentFilter;
    const _filteredRows = getFilteredRows(filterRowsObj as FilterRows);
    setFilteredRows(_filteredRows);
    const _rows = getSlicedRows(_filteredRows, DEFAULT_TABLE_PAGE_SELECT, rowsPerPage);
    setRowsToShowPerPage(_rows);
  };

  const getDateRange = (value: any) => {
    value.from = new Date(value.from / 1000).setHours(0, 0, 0, 0);
    //adding one day extra to filter for same from and to date
    value.to = new Date(value.to / 1000 + 3600 * 1000 * 24).setHours(0, 0, 0, 0);
    handleFilters(TABLE_COLUMN_KEYS.CREATED_DATE, value);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setRequestTypeSelectedOption('');
    setDateRange({ from: '', to: '' });
    setStatusSelectedOption('');
    setUrgentChecked(false);
    setSelectedDateRangeValue('');
    setCurrentPage(DEFAULT_TABLE_PAGE_SELECT);
    filterRowsObj.searchKey = '';
    filterRowsObj.requestTypeFilter = '';
    filterRowsObj.statusFilter = '';
    filterRowsObj.dateFilter = { from: '', to: '' };
    filterRowsObj.urgentFilter = false;
    const _filteredRows = getFilteredRows(filterRowsObj as FilterRows);
    setFilteredRows(_filteredRows);
    const _rows = getSlicedRows(_filteredRows, DEFAULT_TABLE_PAGE_SELECT, rowsPerPage);
    setRowsToShowPerPage(_rows);
  };

  useEffect(() => {
    setCurrentPage(DEFAULT_TABLE_PAGE_SELECT);
    filterRowsObj.searchKey = searchTerm;
    filterRowsObj.requestTypeFilter = requestTypeSelectedOption;
    filterRowsObj.statusFilter = statusSelectedOption;
    filterRowsObj.dateFilter = dateRange;
    filterRowsObj.urgentFilter = urgentChecked;
    const _filteredRows = getFilteredRows(filterRowsObj as FilterRows);
    setFilteredRows(_filteredRows);
    const _rows = getSlicedRows(_filteredRows, DEFAULT_TABLE_PAGE_SELECT, rowsPerPage);
    setRowsToShowPerPage(_rows);
  }, [rows]);

  return (
    <div className="task-list-table">
      <DynamicResultTable
        testId="task-list-table"
        headingText={headingText}
        head={head}
        rows={rowsToShowPerPage}
        emptyStateProps={emptyStateProps}
        isLoading={isLoading}
        totalCount={filteredRows.length}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        isPaginationEnabled={filteredRows.length > 0}
        onRowChange={handleRowsChange}
        showFilters={true}
      >
        {children}
        {searchable && (
          <div className="filters">
            <CustomSearch placeholder={'Search requests'} onSearch={onSearch} isLoading={isLoading} />
            {requestTypeFilterOptions && (
              <RequestTypeFilter
                requestTypeSelectedOption={requestTypeSelectedOption}
                handleFilter={handleFilters}
                requestTypeFilterOptions={requestTypeFilterOptions}
              />
            )}
            {statusFilterOptions && (
              <StatusFilter
                statusSelectedOption={statusSelectedOption}
                handleFilter={handleFilters}
                statusFilterOptions={statusFilterOptions}
              />
            )}
            {showDateFilters && (
              <DateFilter
                dateValue={getDateRange}
                selected={selectedDateRangeValue}
                onSelected={(selected: string) => {
                  setSelectedDateRangeValue(selected);
                }}
              />
            )}
            {showUrgentFilter && <UrgentFilter isChecked={urgentChecked} handleFilter={handleFilters} />}
            {showResetFilter && (
              <Button testId="filter-reset-link" onClick={resetFilters}>
                Reset filters
              </Button>
            )}
          </div>
        )}
        {bulkActions && <div className="bulk-actions">{bulkActions}</div>}
      </DynamicResultTable>
    </div>
  );
};
